import React, { useState, useLayoutEffect, useRef, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { TicketDetail as TicketDetailType, PiiIgnoreEntry, DraftKind, ScaffoldSegment, DeclineInstead, DraftClaim, LetterSlot } from '../types';
import { ClerkTrace, ClerkTraceText, type ClerkTraceKind } from './ClerkTrace';
import { InfoTooltip } from './InfoTooltip';
import { ActionButton, CategoryBadge, DecisionBadges, PanelSection, Spinner, SkeletonBlock, styleRedactionTokens, escapeHtml, escapeHtmlText, isHtml, CrossIcon, CopySmallIcon, SpreadsheetRowIcon, StufeChip, TicketAttributePills, SenderPill, useSenderRegistryServed, PencilIcon, CompareIcon, PaperclipIcon } from './shared';
import { declineInsteadWord } from './DeclineSlot';
import { declineInsteadHue, DECLINE_LABEL } from '../services/declineVerdict';
import { DecisionBadge } from './DecisionBadge';
import { isSettledStatus, showsKopiert } from '../services/settledRow';
import { pillStyle, pillFace } from './SecondaryAxisPills';
import { theme } from '../theme';
import { AI_DESCRIPTOR, NO_DRAFT_FACE, formatPercentFace, formatSecondsFace, copyToastMessage, statusLabel, PIPELINE_STAGE_LABELS, PERSONAL_DATA_LABELS, normalizeKey } from '../constants';
import { SplitPill, SPLIT_PILL_HEIGHT } from './SplitPill';
import { smarttix } from '../services/api';
import { useMaintenanceMode } from '../contexts/MaintenanceContext';
import { useTicketManager } from '../contexts/TicketManagerContext';
import KlaerfallEditor from './KlaerfallEditor';
import { FlagWord, orderedFlags, flagQuoteHover } from './FlagPill';
import { visibleFlags } from '../services/impliedSignals';
import TriageDraftArea, { DRAFT_REFUSAL_TOAST } from './TriageDraftArea';
import PressBar, { SLOT_COLUMNS, type AfterPressView } from './PressBar';
import { letterBarSlots, triageBarSlots, primaryApproveAct, nextKindAct, letterSlotOneFaces, triageSlotOneFaces, type BarSlots, type SlotAct } from '../services/pressBar';
import CompareStage, { type CompareMarks } from './CompareStage';
import LetterCheckFooter from './LetterCheckFooter';
import AttachmentChips from './AttachmentChips';
import { composeCheckPills, letterSlotOfKind, type CheckCardRow, type CheckPill, type FieldsState } from '../services/letterCheck';
import { findTextRange, selectInTextarea } from '../services/textRange';
import { setDisclosed } from '../services/draftDisclosure';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { useKlaerfallDraft } from '../hooks/useKlaerfallDraft';
import { isNoDraft } from '../services/replyEngineMeta';
import { buildDraftModel, claimedKinds, decisionClaimAt, decisionKinds as resolveDecisionKinds, draftKindOrder, draftKindWord, isDraftDecisionKind, isSlotsOpenError, leanKindOf, openSlotCount, openSlotsNote } from '../services/draftClaims';
import { isStaleSuggestionError } from '../services/staleSuggestion';
import type { DraftDecisionKind, DraftEditableKind, DraftSlotKind } from '../services/draftClaims';
import { formatBackendError } from '../utils/formatError';
import { arrivalHoverText } from '../utils/dateFormatting';
import { resolveAttachmentOcrRows, partitionAttachmentRows, countAttachments, attachmentCountTitle, formatBytes, OCR_STATE_LABELS } from '../services/attachmentOcrStatus';

// Why (#5): suggestion HTML carries <span data-edit-start="1"></span>...<span data-edit-end="1"></span>
// markers (migration 039) that bracket the editable Antwortvorschlag region. Split the HTML at those
// markers so the FE can make ONLY the inner span contentEditable (chrome stays immutable).
// Returns null when markers are missing (legacy pre-039 suggestions) — caller hides edit affordance.
const splitEditableRegion = (html: string): { prefix: string; inner: string; suffix: string } | null => {
  const startRe = /<span\s+data-edit-start="1"\s*><\/span>/i;
  const endRe = /<span\s+data-edit-end="1"\s*><\/span>/i;
  const sm = html.match(startRe);
  const em = html.match(endRe);
  if (!sm || !em || sm.index === undefined || em.index === undefined) return null;
  const innerStart = sm.index + sm[0].length;
  const innerEnd = em.index;
  if (innerEnd <= innerStart) return null;
  return {
    prefix: html.slice(0, innerStart),
    inner: html.slice(innerStart, innerEnd),
    suffix: html.slice(innerEnd),
  };
};

interface TicketDetailProps {
  ticket: TicketDetailType;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  onClearError: () => void;
  onApprove: () => void;
  /** The „Nicht hilfreich" press (bottom-bar decision, 2026-10-16); the host owns the mutation and
   *  its post-effects (the press: usePress). Absent ⇒ the slot is mounted but unpressable — never
   *  missing, the bar's own rule. */
  onDecline?: (instead: DeclineInstead | null) => Promise<void>;
  /** One kind's approval, host-owned (the press: optimistic, with its card). Absent ⇒ the claim
   *  runs here as before (handleClaim) and reports through onTriageClaimed. */
  onApproveKind?: (kind: DraftKind) => void;
  /** The after-press card the host's press raised (decide, then step; 2026-10-28). */
  afterPress?: AfterPressView | null;
  /** A refused press, rolled back — said on the bar. */
  pressMessage?: string | null;
  onDismissPressMessage?: () => void;
  /** The press keys (G, N, Enter, Z, Esc, A) are live — the list page and the pop-out, while no
   *  modal stands over the pane. Inactive while a text field has focus. */
  pressKeys?: boolean;
  onReject: () => void;
  onDownload: () => void;
  onGoToUploads?: () => void;
  onReprocess?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  piiIgnoreList?: PiiIgnoreEntry[];
  onPiiIgnore?: (fieldType: string, value: string) => Promise<void>;
  // #5 edit-before-approve — invoked after a successful PATCH so parent reloads ticket.
  onSuggestionEdited?: () => void;
  // Copy-approval: fired when Kopieren is pressed in Offen — copying records an indirect approval.
  // LETTER FLOW ONLY: a triage row has no single "the suggestion" to copy, so its Kopieren row is
  // not rendered at all and the per-draft handler below is what fires instead.
  onCopyApprove?: () => void;
  // Copying ONE draft of a triage row (Part C, 2026-09-07). The host owns the whole flow —
  // clipboard, the copy claim on that kind, and the Rückgängig toast — because only it knows
  // whether a claim is owed (status, maintenance, an existing claim). Absent ⇒ the cards offer no
  // Kopieren at all, which is the honest state for a host that cannot record the credit.
  // Resolves to whether the CLIPBOARD write happened: the card confirms itself on that boolean
  // alone, so a write the browser refused leaves the button unchanged and the host's banner says
  // why. Whether the claim behind it was recorded is the host's business, not the button's.
  onCopyDraft?: (kind: DraftSlotKind, text: string) => Promise<boolean>;
  // A triage claim landed (Part C, 2026-09-07). The MUTATION stays here — the host is told
  // what was settled and how (resultState: 'approved' for an approval of any kind, the archive claim included; 'claimed' for a copy; …) so it can run the same list
  // post-effects a letter approval runs. Absent ⇒ the claim still happens; only the list reaction is
  // skipped, which is the right posture for a host with no lists (the pop-out refetches instead).
  onTriageClaimed?: (kind: DraftKind, resultState: string) => void;
  // An action this component owns failed with a message the clerk must read. The error BANNER is
  // the host's (it owns `error`/`onClearError`), so the message goes up rather than being rendered
  // twice — a second banner here would push the whole detail down.
  onActionError?: (message: string) => void;
  // A write here lost the race against the row it was made from (Part C, 2026-09-07) —
  // approving a draft, composing the second one, or persisting a scaffold fill. NOT reported
  // through onActionError: the remedy is a RELOAD the host performs (it owns the detail it fetched)
  // and states in one toast, not a red banner asking the clerk to try what just lost. Absent ⇒ the
  // outcome is swallowed silently, which is why every host wires it.
  onStaleSuggestion?: () => void;
  // Demo-only: gates the "Simulierte Systemdaten" fill button in the scaffold editor.
  simulateSystemData?: boolean;
  // Tenant setting (operator_config UI_ATTACHMENTS_COUNT_INLINE via /config): whether inline
  // signature-image attachments count toward Anhänge and list as rows. ABSENT ⇒ true — every host
  // threads this from the same App-level /config read (see attachmentsCountInline in App.tsx), and
  // an omitted prop must reproduce the behaviour that shipped before this rule existed, never a
  // silent flip to the new posture.
  attachmentsCountInline?: boolean;
  // Tenant setting (operator_config UI_SHOW_SOURCE_CHANNEL via /config): whether the Eingangsweg row
  // is shown at all. ABSENT ⇒ HIDDEN — every host passes it explicitly; an omitted prop must never
  // reveal the row, because "this host forgot to thread it" and "the tenant asked for it" would then
  // be the same state.
  showSourceChannel?: boolean;
  // Raised when an action declines to complete for a reason the clerk must READ and act on — currently
  // a refused form-row copy. The page owns toasts; this component reports and does not render one, which
  // also keeps an appearing message from shifting the detail layout.
  onInfo?: (message: string) => void;
  // The gap compare mode leaves above itself. ABSENT ⇒ the list window's rule, which clears the
  // sticky NavBar. A host with no NavBar over it — the pop-out — passes COMPARE_STAGE_INSET.
  stageTopInset?: number;
  // Open compare mode whenever a ticket arrives: the pop-out window IS the stage in its own window.
  initialCompare?: boolean;
}

// How long a draft action may run before the surface shows it is held (walk fix round 4,
// 2026-09-09). Long enough that an ordinary round trip on a healthy link never dims anything, short
// enough that a clerk who is actually waiting is told why well before they press again.
const DRAFT_HOLD_VISIBLE_MS = 250;

// The header (registry `ticketDetail_face`, rule 8 „born from the row click", 2026-10-31): four
// fixed lines, 111 px on every ticket at 546 px content width. Lines 1–2 the subject (48 px
// reserved); line 3 the meta line (what the mail is, its warnings, who sent it, the values, the
// paperclip); line 4 the reason line's slot (blank until it lands) and Klassifikationskonfidenz with
// the retry count. Nothing wraps; only the values truncate; nothing moves between tickets.
const SUBJECT_LINE_HEIGHT = 24;
const HEADER_LINE_HEIGHT = 20;
const PAPERCLIP_SLOT_WIDTH = 34;
const headerLineStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: theme.spacing.sm, minWidth: 0,
  height: HEADER_LINE_HEIGHT, marginTop: 6, whiteSpace: 'nowrap',
  fontSize: 12, color: theme.colors.navyMuted,
};
// The meta line's left group: every item keeps its width except the values, which take the rest
// and truncate from the right (flex 1 1 0) — the first and only slot that gives way.
const headerMetaGroupStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: theme.spacing.sm, minWidth: 0, flex: 1, overflow: 'hidden',
};
const miniButtonStyle: React.CSSProperties = {
  fontFamily: theme.font.family, fontSize: 11.5, fontWeight: 600, color: theme.colors.navyMid,
  backgroundColor: theme.colors.white, border: `1px solid ${theme.colors.borderLight}`,
  borderRadius: 5, padding: '1px 9px', cursor: 'pointer', lineHeight: '18px',
};
// The door into compare mode (the picked `.door`): a word button with its icon, 1.5 px navy frame.
const doorSegmentStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, border: 'none', backgroundColor: 'transparent',
  fontFamily: theme.font.family, fontSize: 11.5, fontWeight: 600, color: theme.colors.navy,
  cursor: 'pointer', lineHeight: '18px',
};
const mailBoxStyle: React.CSSProperties = {
  padding: theme.spacing.md, backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`,
  fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
};

// The subject, two lines at most; the sender address and the arrival show on hover or focus
// (after the house tooltip delay), never as a line of their own.
const SubjectLine: React.FC<{ subject: string; hover: string; copy?: boolean; fixedHeight?: boolean }> = ({ subject, hover, copy, fixedHeight = true }) => {
  const [show, setShow] = useState(false);
  // The compare stage repeats the header: its copy is no second heading and owns no ids.
  const Tag = copy ? 'div' : 'h2';
  const hoverId = copy ? undefined : 'detail-subject-hover';
  return (
    <div style={{ position: 'relative' }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)} onBlur={() => setShow(false)}>
      <Tag
        tabIndex={0}
        aria-describedby={hover && show ? hoverId : undefined}
        style={{
          margin: 0, fontSize: 17, fontWeight: 600, lineHeight: `${SUBJECT_LINE_HEIGHT}px`, color: theme.colors.navy, outline: 'none',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          // Two lines reserved on every ticket (measured need: about half the demo subjects), so a
          // short subject never pulls the lines below it up.
          height: fixedHeight ? SUBJECT_LINE_HEIGHT * 2 : undefined,
        }}
      >{subject}</Tag>
      {hover && show && (
        <span id={hoverId} role="tooltip" data-testid={copy ? undefined : 'detail-subject-hover'} style={{
          position: 'absolute', left: 0, top: '100%', marginTop: 4, zIndex: 8, pointerEvents: 'none',
          backgroundColor: theme.colors.navy, color: theme.colors.white, fontSize: 11.5, padding: '4px 9px',
          borderRadius: 6, whiteSpace: 'nowrap', animation: 'stx-fadeIn 0.15s ease 0.45s both',
        }}>{hover}</span>
      )}
    </div>
  );
};

// The second-axis values as plain text in served order, joined by „ · " — the meta line's one
// truncating slot. The hover (the house tooltip, 450 ms) carries every value and mounts only when
// the text is cut: a bubble repeating the visible line would say nothing twice. When cut it is a
// keyboard stop (InfoTooltip makes its anchor focusable), so the hidden values reach every clerk.
const HeaderValues: React.FC<{ faces: string[] }> = ({ faces }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [cut, setCut] = useState(false);
  const text = faces.join(' · ');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setCut(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  const span = (
    <span ref={ref} data-testid="detail-values" style={{
      flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      fontSize: 12, color: theme.colors.navyMuted, outline: 'none',
    }}>{text}</span>
  );
  return cut ? <InfoTooltip text={text}>{span}</InfoTooltip> : span;
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });


const TicketDetailComponent: React.FC<TicketDetailProps> = ({
  ticket, loading, actionLoading, error, onClearError, onApprove, onReject, onDownload, onGoToUploads, onReprocess, onArchive, onRestore,
  piiIgnoreList, onPiiIgnore, onSuggestionEdited, onCopyApprove, onCopyDraft, simulateSystemData, showSourceChannel, onInfo,
  onTriageClaimed, onActionError, onStaleSuggestion, attachmentsCountInline = true, stageTopInset, onDecline,
  onApproveKind, afterPress, pressMessage, onDismissPressMessage, pressKeys, initialCompare,
}) => {
  // During maintenance the server 423s all writes; disable the per-ticket write
  // actions so the banner's "Aktionen pausiert" is literal (no optimistic-remove
  // → 423 → reappear flicker). Read-only actions (download, navigation) stay live.
  const maintenance = useMaintenanceMode();
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const senderRegistryServed = useSenderRegistryServed();
  // ─── Compare mode (2026-10-30) ───────
  // The stage where mail and letter stand side by side; it replaced the KI-Vorschlag maximize. One
  // boolean says whether it is open; `marks` is the pair a card row pointed at (marks live only in
  // the stage, never in the everyday view); `pendingSelect` is the letter sentence Bearbeiten
  // selects when the editor opens from the stage.
  const [compareOpen, setCompareOpen] = useState(false);
  const [marks, setMarks] = useState<CompareMarks | null>(null);
  const pendingSelect = useRef<string | null>(null);
  const [chipFocusSeq, setChipFocusSeq] = useState(0);
  // The pill whose card row opened compare mode stays filled there until Esc or another pill.
  const [pinnedPill, setPinnedPill] = useState<CheckPill['kind'] | null>(null);
  const closeCompare = useCallback(() => { setCompareOpen(false); setMarks(null); setPinnedPill(null); pendingSelect.current = null; }, []);
  const openCompare = useCallback((pair?: { mail: string | null; letter: string | null }) => {
    setCompareOpen(true);
    setMarks(prev => (pair ? { mail: pair.mail, letter: pair.letter, seq: (prev?.seq ?? 0) + 1 } : null));
  }, []);
  // The letter shown on a row that carries two (WIM reply + forward): the switch above the letter and
  // the slot-1 verb are one choice. Keyed to the ticket, so a new row starts on its own lead.
  const [shownPick, setShownPick] = useState<{ uuid: string; kind: DraftDecisionKind } | null>(null);
  // The disabled Genehmigen is hovered: the „Felder" pill shows itself as the cause.
  const [approveHover, setApproveHover] = useState(false);
  // The kind whose claim/download is on the wire (fix round 1, 2026-09-09). It lives HERE, beside
  // the host's other in-flight state, and not inside the bar: the bar REMOUNTS when it
  // moves into the maximized KI stage's footer, and a flag held inside it was cleared by that move
  // — re-arming a button the clerk had already submitted. Deliberately NOT actionLoading: that flag
  // belongs to the host's own mutations, and one claim in flight must not freeze the whole bar.
  const [busyDraftKind, setBusyDraftKind] = useState<DraftKind | null>(null);
  // The hold's LOOK, held back ~250ms behind the hold itself (walk fix round 4, 2026-09-09). The
  // refusal above is immediate and stays that way — a debounced refusal would let a double-submit
  // through, which is the whole reason the flag exists. What the walk found was the dim: on a fast
  // round trip every draft face washed out and back in inside a couple of frames, which reads as a
  // glitch rather than as a state. So the surface goes visibly held only if the action is STILL in
  // flight when the window elapses, and an action nobody perceived shows nobody anything.
  // Cleared synchronously with the flag (no trailing fade-out): the moment the action lands the
  // clerk may act again, and a face that stayed dim would be lying in the other direction.
  const [holdVisible, setHoldVisible] = useState(false);
  // ─── The press keys (decide, then step; 2026-10-28) ───
  // G Genehmigen (slot 1's approval), N Nicht hilfreich (opens the chooser); while the after-press
  // card stands: Enter Nächste Mail, Esc Hier bleiben, A the other letter (WIM); Z Rückgängig while
  // it stands. Taken at the document, capture phase, so a focused button cannot swallow Enter into a
  // second press; never while a text field has focus, a modifier is held, or a list (the caret's,
  // the chooser) is open — those own their keys.
  const [declineRequest, setDeclineRequest] = useState(0);
  const onActRef = useRef<(act: SlotAct) => void>(() => {});
  const pressSlotsRef = useRef<BarSlots | null>(null);
  const cardRef = useRef<AfterPressView | null>(null);
  const keysLive = !!pressKeys && !loading && !!ticket;
  useEffect(() => {
    if (!keysLive) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[data-testid="press-slot-menu"],[data-testid="decline-chooser"]')) return;
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const take = (fn: () => void) => { e.preventDefault(); e.stopPropagation(); fn(); };
      const card = cardRef.current;
      if (card) {
        if (k === 'z' && card.undoLive) { take(card.onUndo); return; }
        if (card.mode === 'full') {
          if (k === 'Enter' && card.onNext) { take(card.onNext); return; }
          if (k === 'Escape') { take(card.onStay); return; }
          if (k === 'a' && card.nextKind) { take(card.nextKind.onPress); return; }
          return;
        }
      }
      if (k === 'g') {
        const act = primaryApproveAct(pressSlotsRef.current);
        if (act) take(() => onActRef.current(act));
        return;
      }
      if (k === 'n' && pressSlotsRef.current?.[2]?.type === 'decline') take(() => setDeclineRequest(n => n + 1));
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [keysLive]);

  useEffect(() => {
    if (busyDraftKind === null) { setHoldVisible(false); return; }
    const timer = setTimeout(() => setHoldVisible(true), DRAFT_HOLD_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [busyDraftKind]);
  // The footer's in-flow home. While compare mode owns the footer (letter check + buttons) it is
  // rendered THERE and nowhere else (one Genehmigen, never two), so this slot holds the height it
  // had — the same measured-placeholder mechanic the stage uses for the letter, for the same
  // reason: closing must land on the pane the clerk left.
  const bottomBarSlotRef = useRef<HTMLDivElement>(null);
  const bottomBarHeight = useRef(0);
  // Same mechanic for the action-error banner, which moves into the stage's pinned top slot for
  // the same reason the bar moves into its footer: a failure the clerk caused inside the stage has
  // to be readable inside it. The slot holds the height it had so the page underneath does not
  // rise as the banner leaves it.
  const errorSlotRef = useRef<HTMLDivElement>(null);
  const errorHeight = useRef(0);
  // Measured continuously while the bar is in the flow — after the hand-over the slot is empty,
  // so measuring then would read zero.
  useLayoutEffect(() => {
    if (!compareOpen && bottomBarSlotRef.current) bottomBarHeight.current = bottomBarSlotRef.current.offsetHeight;
    if (!compareOpen && errorSlotRef.current) errorHeight.current = errorSlotRef.current.offsetHeight;
  });
  // ticket?.-safe like every other derivation above the `if (!ticket) return null` guard below:
  // this runs on the null-ticket render the host pages (PopoutShell/Antworten/Archiv) pass through
  // while a selection is clearing, before that guard short-circuits.
  const anhaengeRows = resolveAttachmentOcrRows(ticket?.attachments, ticket?.ocrDecisions);
  const { documents: anhaengeDocuments, inline: anhaengeInline } = partitionAttachmentRows(anhaengeRows);
  // The count beside „Vergleichen" — the same rule the Anhänge title used, the tenant setting honoured.
  const anhaengeCount = countAttachments(anhaengeRows, attachmentsCountInline);
  const extractionSummary = ticket?.suggestion?.extractionSummary ?? null;
  const [btnFade, setBtnFade] = useState<'in' | 'out'>('in');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [rowFeedback, setRowFeedback] = useState(false);
  const [rowError, setRowError] = useState(false);
  const btnKeyRef = useRef('');
  const [dismissedPii, setDismissedPii] = useState<Set<string>>(new Set());
  const prevTicketUuid = useRef('');

  // ─── #5 edit-before-approve state ────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const editableRef = useRef<HTMLDivElement>(null);
  // ONE cancel, two doors: the header's Abbrechen and the editor's own pair both call it.
  const cancelEdit = () => { editFocusArmed.current = true; setEditMode(false); setEditError(null); };

  // Reset edit mode when the ticket changes — and close compare mode with it (marks included): a
  // stage that survived a selection change would show the new mail under the old one's marks.
  // A host can ask for the stage open on arrival (the pop-out window is this stage).
  useEffect(() => {
    setEditMode(false);
    setEditSaving(false);
    setEditError(null);
    if (initialCompare && ticket?.uuid) openCompare(); else closeCompare();
  }, [ticket?.uuid, closeCompare, openCompare, initialCompare]);

  // VNB scaffold hook — unconditional (rules-of-hooks). Non-scaffold tickets pass [] so
  // allConfirmed=true, openCount=0 — the !scaffold guard in canApprove short-circuits anyway.
  const scaffold = ticket?.suggestion?.scaffold ?? null;
  // Key the draft by the SUGGESTION attempt (ai_suggestions.id), not the ticket uuid — so a
  // reanalysis (a new suggestion under the same uuid) resets the fill-in to a clean slate
  // instead of bleeding the prior attempt's drafts into the fresh scaffold.
  const draftKey = ticket?.suggestion?.id != null ? `sug-${ticket.suggestion.id}` : (ticket?.uuid ?? '');
  const draft = useKlaerfallDraft(
    draftKey,
    scaffold?.segments ?? [],
    async (text, scaffoldSegments) => {
      setEditSaving(true); setEditError(null);
      // suggestionId (fix-review round 1): ticket.suggestion.id IS the draftKey's numeric source
      // (see draftKey above) — read straight from the closure rather than re-derived, so the
      // server merges into the EXACT row this fill was confirmed against.
      const res = await tm.editSuggestion(ticket.uuid, text, scaffoldSegments, ticket.suggestion?.id);
      setEditSaving(false);
      // Stale FIRST: the server merged into nothing because this fill was confirmed against a row
      // that is no longer current. The host reloads; an inline persist error would tell the clerk
      // to re-confirm against the very draft that is gone.
      if (isStaleSuggestionError(res)) { onStaleSuggestion?.(); return false; }
      if (res.success) { onSuggestionEdited?.(); return true; }
      setEditError(res.error || 'Fehler beim Speichern'); return false;
    },
  );

  // Simulierte Systemdaten (demo): ask the BE for deterministic values for the empty system slots,
  // then fill them through the SAME draft path a rep uses (fillSystemSlots → editSuggestion). Returns
  // the filled segment indices so the editor can flash them. Never overwrites mail-filled slots (the
  // BE only resolves placeholder slots; we match returned slotIds back to their segment index).
  const handleSimulate = useCallback(async (): Promise<number[]> => {
    if (!ticket) return [];
    const segs = scaffold?.segments ?? [];
    const res = await smarttix.simulateSystemData(ticket.uuid);
    if (!res.success) { setEditError(res.error || 'Fehler beim Abrufen der Systemdaten'); return []; }
    const fills = (res.filledSlots ?? [])
      .map(fs => {
        const index = segs.findIndex(s => s.kind === 'slot' && s.slotId === fs.slotId);
        return index >= 0 ? { index, value: fs.value } : null;
      })
      .filter((x): x is { index: number; value: string } => x !== null);
    // Deliberate ~0.55s beat so the fill reads as a system lookup returning results, not an instant
    // form auto-fill — the "generic → concrete because we queried the system" gesture.
    await new Promise(r => setTimeout(r, 550));
    await draft.fillSystemSlots(fills);
    return fills.map(f => f.index);
  }, [ticket, scaffold, draft]);

  // Composes the row's OTHER draft on demand (Part C, 2026-09-07). Same immediate
  // refetch every other mutation on this pane uses — without it the composed draft would only
  // reach this window on the next background poll tick.
  const handleRenderDraft = useCallback(async (kind: DraftSlotKind) => {
    if (!ticket?.suggestion) return { success: false };
    const res = await tm.renderSecondaryDraft(ticket.uuid, ticket.suggestion.id, kind);
    // Three outcomes, three different things to say — and only the last of them is an error.
    // Stale (409): the host reloads and says so; the strip stays silent (it returns the result, so
    // the strip reads the same flag). Refused (422): the composer had nothing to produce, which the
    // strip states in place AND the host names in a toast, since the cause is a missing template
    // rather than anything the clerk did.
    if (isStaleSuggestionError(res)) { onStaleSuggestion?.(); return res; }
    if (res.success) onSuggestionEdited?.();
    else if (res.refused) onInfo?.(DRAFT_REFUSAL_TOAST);
    return res;
  }, [ticket, tm, onSuggestionEdited, onStaleSuggestion, onInfo]);

  // Rewrites ONE composed draft slot (Part C wave 2, 2026-09-09). Same shape as handleClaim below
  // and for the same reasons: this component holds the suggestion row the clerk is reading, so the
  // write goes against THAT row's id, and it owns busyDraftKind so a save cannot be submitted
  // twice — or approved out from under — while it is on the wire.
  // The three 409s part ways here: only the moved-on row reloads. The other two are refusals the
  // CARD states in its own stage, because the clerk still has their text and the sentence tells
  // them what to do with it — a toast plus a closed editor would take the words away.
  // Narrowed to the kinds core will take (DRAFT_EDITABLE_KINDS, the mirror of its own list):
  // the card mounts no Bearbeiten for the others, and the type says so rather than leaving the
  // refusal to a 400.
  // `scaffoldSegments` present ⇒ this is a SLOT FILL rather than a free-text rewrite (Task 6b):
  // the same endpoint and the same row identity, with the letter's segments beside the assembled
  // text so the server rewrites the scaffold and the next read serves the fill back. One write
  // path for both gestures, because they are one gesture — the clerk finishing a draft.
  const handleEditDraft = useCallback(async (
    kind: DraftEditableKind, text: string, scaffoldSegments?: ScaffoldSegment[],
  ) => {
    if (!ticket?.suggestion) return { success: false };
    setBusyDraftKind(kind);
    try {
      const res = await tm.editDraftSlot(ticket.uuid, {
        suggestionId: ticket.suggestion.id, kind, text, scaffoldSegments,
      });
      if (isStaleSuggestionError(res)) { onStaleSuggestion?.(); return { success: false, stale: true }; }
      // 204 No Content — the new text and the new stamp arrive through the refetch, never from the
      // answer, so there is nothing to hand back but the verdict.
      if (res.success) onSuggestionEdited?.();
      return res;
    } finally {
      setBusyDraftKind(null);
    }
  }, [ticket, tm, onSuggestionEdited, onStaleSuggestion]);

  // Kopieren is a draft MUTATION, and it took the round-3 review to say so out loud: a copy CLAIMS
  // the slot (claimDraft via='copy') and earns the row its approval credit — the whole reason
  // copy_approved_at exists. It ran outside the in-flight flag entirely, so a clerk could copy one
  // kind while a save on another was on the wire, and each would clear a flag the other had set.
  // Wrapped HERE rather than in each host's own handler: both hosts (the main page and the pop-out
  // window) mount this component and hand their copy handler down through this one prop, so one
  // wrapper serializes the copy with the claims, the saves and the downloads on both surfaces —
  // and neither host's own error/toast behaviour is touched, because the wrapper only brackets it.
  // The flag covers the CLIPBOARD write too, not just the claim: it is the same gesture, it is
  // brief, and a hold that started halfway through would leave a window where two draft actions
  // genuinely overlap.
  const handleCopyDraft = useCallback(async (kind: DraftSlotKind, text: string) => {
    if (!onCopyDraft) return false;
    setBusyDraftKind(kind);
    try { return await onCopyDraft(kind, text); }
    finally { setBusyDraftKind(null); }
  }, [onCopyDraft]);

  // Records the clerk's decision for ONE kind (Part C, 2026-09-07). The mutation lives
  // here, beside handleRenderDraft, for the same reason: this component holds the suggestion row
  // the clerk is looking at, so the claim goes against THAT row's id and not whichever is newest.
  // ⚠ EVERY draft action in this file owns the in-flight flag (busyDraftKind) — the two claims
  // below, the slot edit, the copy above and the download — set on entry, cleared in a finally, so
  // an early return or a throw can never strand a permanently disabled slot. Not "every write":
  // the download is a read and holds it too, because what the flag serializes is the SURFACE, and a
  // download firing under an in-flight claim would hand the clerk an artifact that claim may be
  // about to change. The bar reads it as a prop; see busyDraftKind's declaration for why it cannot
  // live in the bar.
  const handleClaim = useCallback(async (kind: DraftKind) => {
    if (!ticket?.suggestion) return;
    setBusyDraftKind(kind);
    try {
      const res = await tm.claimDraft(ticket.uuid, ticket.suggestion.id, kind, 'approve');
      // Stale FIRST — before the refusal reading below. The server's own German for this case tells
      // the clerk to reload; the host does exactly that, so repeating the sentence in a red banner
      // would ask for work that has already been done.
      if (isStaleSuggestionError(res)) { onStaleSuggestion?.(); return; }
      // The letter still has gaps as the SERVER reads the row — a race this surface's own slot
      // refusal cannot cover, because it refuses from the scaffold it was served and that scaffold
      // is the thing that is behind. The remedy is the reload: the refreshed row puts „Noch n
      // Felder offen" back on the slot from served data, so nothing here composes a sentence and
      // the server's own German goes to the banner exactly as every other refusal's does.
      if (!res.success && isSlotsOpenError(res)) {
        onSuggestionEdited?.();
        onActionError?.(res.error ?? 'Die Aktion konnte nicht ausgeführt werden.');
        return;
      }
      if (res.success) {
        onSuggestionEdited?.();
        onTriageClaimed?.(kind, res.resultState ?? 'approved');
      } else {
        // A refusal is the server's own German (ineligible kind, unrendered draft, a row that moved
        // on) — it goes to the host's banner verbatim rather than being re-worded here.
        onActionError?.(res.error ?? 'Die Aktion konnte nicht ausgeführt werden.');
      }
    } finally {
      setBusyDraftKind(null);
    }
  }, [ticket, tm, onSuggestionEdited, onTriageClaimed, onActionError, onStaleSuggestion]);

  // The approved artifact as an .eml. A read, so it has no result envelope — only a throw.
  // Only a DECISION kind is ever downloadable: the bar hands out an .eml for the kind it settled,
  // and the receipt and the Auskunft letter are settled (or not offered) elsewhere.
  const handleTriageDownload = useCallback(async (type: DraftDecisionKind) => {
    if (!ticket) return;
    setBusyDraftKind(type);
    try { await smarttix.downloadTriageDraft(ticket.uuid, type); }
    catch (err: any) { onActionError?.(formatBackendError(err, 'Herunterladen des Entwurfs')); }
    finally { setBusyDraftKind(null); }
  }, [ticket, onActionError]);

  // The draft slot model (Part C, 2026-09-07) — a pure fold, not a hook, and `ticket?.`-safe
  // like every other derivation above the null guard. Folded ONCE per render: the draft area and the
  // bottom bar both read THIS object, so they can never disagree about what the row offers.
  // The tenant's own slot order, derived ONCE here (2026-09-08) and handed to the fold: the served
  // primary-axis values in served order, mapped kind -> handling through the served
  // `decisionKindHandling` (part 4c, 2026-09-27 — was the code constant `CLAIM_KIND_HANDLING`).
  // While the first /config fetch is still in flight the accessors have nothing trustworthy to say,
  // so the fold takes the default layout for that window rather than an order built from a
  // pre-fetch default — same posture every other vocab-dependent surface takes. An older backend
  // (generation 9, no field) serves `decisionKindHandling: {}`, which resolves nothing and falls to
  // the same default layout, never a guess.
  const draftKindSlotOrder = useMemo(
    () => draftKindOrder(cm.configLoading ? null : cm.categoryKeys, cm.decisionKindHandling),
    [cm.configLoading, cm.categoryKeys, cm.decisionKindHandling],
  );
  // The DETAIL's own receipt rides in as the fourth input (2026-09-17): it is stateless and is
  // composed by this very read, so the ack slot is filled from it rather than waiting for a draft
  // row that is never written. Absent on an older core — the fold then reads the postures alone.
  // The fifth input (R38, part 5-2): the classifier's lean, mapped to its letter kind through the
  // served map — on a Prüfen row with a Begründung it leads the walk and every letter rests.
  const draftModel = buildDraftModel(
    ticket?.suggestion, ticket?.draftPostures, draftKindSlotOrder, ticket?.receipt,
    leanKindOf(ticket?.suggestion, cm.decisionKindHandling));

  // The row's composed letters (WIM: a reply and a forward can both stand). One is shown at a time:
  // the clerk's pick, else the KI's lead, else the first. A letter composed while the row is open
  // takes the stage (the clerk asked for it by pressing its strip).
  // Every composed letter counts, a resting one too (the switch opens it).
  const letterKinds = (ticket?.suggestion?.triage ? draftModel.slots.filter(slot => slot.text !== null) : [])
    .map(slot => slot.kind).filter(isDraftDecisionKind);
  const letterKindsSig = letterKinds.join(',');
  const prevLetterKinds = useRef<{ uuid: string; kinds: string[] }>({ uuid: '', kinds: [] });
  useEffect(() => {
    const uuid = ticket?.uuid ?? '';
    const prev = prevLetterKinds.current;
    const kinds = letterKindsSig ? letterKindsSig.split(',') : [];
    if (prev.uuid === uuid) {
      const arrived = kinds.find(k => !prev.kinds.includes(k));
      if (arrived && prev.kinds.length > 0) setShownPick({ uuid, kind: arrived as DraftDecisionKind });
    }
    prevLetterKinds.current = { uuid, kinds };
  }, [ticket?.uuid, letterKindsSig]);
  const leadLetter = draftModel.leadKind && isDraftDecisionKind(draftModel.leadKind) && letterKinds.includes(draftModel.leadKind)
    ? draftModel.leadKind : letterKinds[0] ?? null;
  const shownKind: DraftDecisionKind | null = shownPick && shownPick.uuid === ticket?.uuid && letterKinds.includes(shownPick.kind)
    ? shownPick.kind : leadLetter;

  // V opens (and closes) compare mode — never while a text field has focus or another modal stands.
  const compareKeyRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!ticket || loading) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || (e.key !== 'v' && e.key !== 'V')) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const otherModal = Array.from(document.querySelectorAll('[aria-modal="true"]'))
        .some(el => el.getAttribute('data-testid') !== 'compare-stage');
      if (otherModal) return;
      e.preventDefault();
      compareKeyRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ticket, loading]);
  compareKeyRef.current = () => { if (compareOpen) closeCompare(); else openCompare(); };

  // A WIM card's Bearbeiten pressed in compare mode: its textarea opens with the marked sentence
  // selected (the card owns its editor, so the pane finds it once it is there).
  const [cardSelect, setCardSelect] = useState<{ quote: string; seq: number } | null>(null);
  useEffect(() => {
    if (!cardSelect || !ticket) return;
    const root = document.querySelector(`[data-letter-area="${ticket.uuid}"]`);
    const ta = Array.from(root?.querySelectorAll<HTMLTextAreaElement>('textarea[data-testid^="draft-edit-"]') ?? [])
      .find(el => !el.closest('[hidden]'));
    if (!ta) return;
    setCardSelect(null);
    ta.focus({ preventScroll: true });
    selectInTextarea(ta, cardSelect.quote);
  }, [cardSelect, ticket]);

  // Entering or leaving the editor moves no scroll: nothing here writes a scroll position (the
  // scroll boxes opt out of anchoring instead). Focus is handed over explicitly and without a
  // scroll — into the editor on entry, back to Bearbeiten on exit — so it never falls off the
  // now-hidden button. In compare mode only the marked-sentence selection below may move its column.
  const bearbeitenRef = useRef<HTMLButtonElement>(null);
  const editFocusArmed = useRef(false);
  useLayoutEffect(() => {
    if (!editFocusArmed.current) return;
    editFocusArmed.current = false;
    if (editMode) {
      if (!pendingSelect.current) editableRef.current?.focus({ preventScroll: true });
    } else {
      bearbeitenRef.current?.focus({ preventScroll: true });
    }
  }, [editMode]);

  // Bearbeiten opened from compare mode: the marked letter sentence is selected in the editor.
  useEffect(() => {
    const quote = pendingSelect.current;
    if (!editMode || !quote) return;
    pendingSelect.current = null;
    const el = editableRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    const range = findTextRange(el, quote);
    const sel = window.getSelection?.();
    if (range && sel) { sel.removeAllRanges(); sel.addRange(range); }
  }, [editMode]);

  // All hooks before early returns (React rules-of-hooks)
  // Why: 2-slot model — key changes when slot content changes, triggering crossfade.
  // The key is the bar's FACE INPUTS, nothing wider (2026-09-07). A claim only changes a face when
  // it was made by APPROVAL — that is what closes a claim slot and turns it into a download — so
  // the key carries kind+via for the approve claims only. Keying on every claim KEY made a copy
  // claim (which changes nothing on this bar; the row stays Offen with the same three faces) fade
  // the whole bar out and back for no visible reason, while a copy→approve UPGRADE — same key,
  // different via — changed a face and did not fade at all, which is the case that matters.
  const approveClaimKey = Object.entries(draftModel.claims)
    .filter(([, claim]) => claim?.via === 'approve')
    .map(([kind, claim]) => `${kind}:${claim?.via ?? 'none'}`)
    .sort()
    .join(',');
  const btnKey = ticket
    ? `${ticket.status}-${ticket.previousStatus ?? ''}-${ticket.hasDraft}` +
      `-${approveClaimKey}` +
      `-${draftModel.rendered.map(s => s.kind).join(',')}`
    : '';
  useLayoutEffect(() => {
    if (btnKeyRef.current && btnKey !== btnKeyRef.current) {
      setBtnFade('out');
      const t = setTimeout(() => { btnKeyRef.current = btnKey; setBtnFade('in'); }, 150);
      return () => clearTimeout(t);
    }
    btnKeyRef.current = btnKey;
  }, [btnKey]);
  useLayoutEffect(() => {
    if (ticket && ticket.uuid !== prevTicketUuid.current) {
      prevTicketUuid.current = ticket.uuid;
      setDismissedPii(new Set());
    }
  }, [ticket]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={32} label="Lade Details..." />
      </div>
    );
  }

  if (!ticket) return null;

  // D3: a deliberate engine no-draft (empty suggestion + reply_engine envelope) blocks all three
  // draft actions — the Step-9 .eml blob exists but is EMPTY; approving/copying/downloading it is
  // a footgun. Single predicate, shared by render branch + guards.
  const noDraft = isNoDraft(ticket?.suggestion);
  // A triage suggestion (wim) has NO letter draft — it carries the row's own draft slots instead,
  // rendered by the draft area above. isNoDraft cannot say so (it is replyEngine-scoped and
  // returns false without an envelope), which is how a triage ticket rendered an enabled
  // Genehmigen that "approved" an empty non-draft and credited Abnahmequote (operator repro
  // 2026-08-31). The letter-flow slot-1 button is suppressed for triage tickets instead.
  const isTriage = !!ticket?.suggestion?.triage;
  // A letter-flow row whose pipeline composed NO letter (part 5-2): a flag decided „no letter" on
  // this mail — the engine path flags it as noDraft above, the K path writes an empty row without an
  // envelope. Read off the COMPOSED text and the scaffold, never the clerk's edit: a cleared edit is
  // a letter the clerk emptied, and a scaffold letter carries its text in its segments.
  const sugg0 = ticket.suggestion ?? null;
  const letterBlank = !!sugg0 && !isTriage && !sugg0.scaffold?.segments?.length && !sugg0.suggestionText?.trim();
  // …and when the Begründung PROPOSES the filing, the Begründung is the Vorschlag (board 3 Q3, R36.2):
  // approving it is a real decision — core records it as the archive claim — so slot 1 is live.
  // Its word is plain „Genehmigen" (operator, 2026-10-19, overruling R36.2's „<archive word>
  // genehmigen" HERE only): the letter bar's yes verb just goes, on every row; what is approved
  // (no letter, file it) is the pane's job to show. The triage bar keeps „<Kind> genehmigen",
  // because there a row carries several drafts and the verb must name which one.
  const fileProposal = letterBlank && !!sugg0?.reasoning?.proposal;
  const canApprove = ticket.status === 'suggestion_ready' && (!scaffold || draft.allConfirmed) && (!noDraft || fileProposal) && !isTriage;
  // Demo-only: show the fill button only while there are still-empty system (placeholder) slots and
  // the ticket is open. Keys off draft state (not segment.status) so it hides once filled/approved.
  const canSimulate = !!simulateSystemData && ticket.status === 'suggestion_ready' && !!scaffold &&
    scaffold.segments.some((s, i) => s.kind === 'slot' && s.status === 'placeholder' && !draft.isConfirmed(i));
  // Genehmigen settles the row (decision 2026-10-23): the bar's shape follows the row's state
  // {classified, settled, archived} — open, settled (approved/sent/declined: Entschieden), filed.
  const settled = isSettledStatus(ticket.status);
  const archived = ticket.status === 'archived';
  // The standing or filed „Nicht hilfreich" verdict — the decision badge on the header line.
  const declined = ticket.status === 'declined' || (archived && ticket.previousStatus === 'declined');
  const declineWord = declined ? declineInsteadWord(cm, ticket.decline) : null;
  // What the yes YIELDED: a letter to download, or nothing (approved without a letter — the filing
  // proposal, a no-letter row — or an engine no-draft whose .eml is empty).
  const approvedLetter = settled && !declined && ticket.hasDraft && !noDraft && !letterBlank;
  const canReprocess = ticket.status === 'pipeline_error' && !!onReprocess;
  const canArchive = (settled || ticket.status === 'pipeline_error') && !!onArchive;
  const canRestore = archived && !!onRestore;
  const isPipelineError = ticket.status === 'pipeline_error';
  const stageLabel = ticket.pipelineStage ? (PIPELINE_STAGE_LABELS[ticket.pipelineStage] || ticket.pipelineStage) : undefined;

  // Actions — THE bottom bar (decide, then step; decision 2026-10-28). An open or decided row gets
  // the PressBar: three fixed slots, letter flow and triage alike (services/pressBar) —
  //   slot 1 = the next open act on this mail, else the letter's download, else blank (width kept);
  //   slot 2 = Neu analysieren;  slot 3 = Nicht hilfreich (open) → Archivieren (decided).
  // A slot's label cross-fades when its act changes; the bar never blanks and never spins. Every
  // other status keeps its own short bar: a filed row (Archiv) is Entwurf herunterladen ·
  // Wiederherstellen, a failed row Erneut verarbeiten · Archivieren — those cross-fade whole on a
  // status change (btnKey).
  // Bound to a const because compare mode pins THIS NODE as its footer: the footer moves between
  // two homes, and building it twice would put two Genehmigen buttons on one screen.
  const writesBlocked = actionLoading || maintenance;
  const pressSlots: BarSlots | null = isTriage
    ? (cm.configLoading ? null : triageBarSlots({
      status: ticket.status, model: draftModel, face: cm.face, maintenance,
      writesBlocked, draftBusy: busyDraftKind !== null, holdVisible, canArchive: canArchive, shownKind,
    }))
    : letterBarSlots({
      status: ticket.status, canApprove, noDraft: noDraft && !fileProposal,
      openFields: scaffold && !draft.allConfirmed ? draft.openCount : 0,
      approvedLetter, canArchive, writesBlocked, maintenance,
    });
  const triageLoading = isTriage && cm.configLoading && (ticket.status === 'suggestion_ready' || settled);
  const approveKind = onApproveKind ?? ((kind: DraftKind) => { void handleClaim(kind); });
  const onAct = (act: SlotAct) => {
    switch (act.type) {
      case 'approve': onApprove(); return;
      case 'approveKind': approveKind(act.kind); return;
      case 'download': onDownload(); return;
      case 'downloadKind': void handleTriageDownload(act.kind); return;
      case 'retry': onReject(); return;
      case 'archive': onArchive?.(); return;
    }
  };
  onActRef.current = onAct;
  pressSlotsRef.current = pressSlots;
  // The card's A (WIM): while the full card stands over an open row's yes, the other letter.
  const nextKind = afterPress && afterPress.mode === 'full' && afterPress.side === 'left' ? nextKindAct(pressSlots) : null;
  const cardView: AfterPressView | null = afterPress
    ? { ...afterPress, nextKind: nextKind ? { label: nextKind.label, onPress: () => approveKind(nextKind.kind) } : null }
    : null;
  cardRef.current = cardView;

  // ─── What the row carries ───
  const generating = ['reanalyzing', 'categorizing', 'categorized', 'generating_suggestion'].includes(ticket.status);
  // A row no composer wrote a letter for — the engine's deliberate no-draft (envelope + empty text)
  // or the K path's empty row — on every status.
  const noLetter = noDraft || letterBlank;
  // The pane reads LETTER FIRST on a row that has one (or is being written); a row without a letter
  // reads its mail as the body, with the Begründung's summary as one line under it (2026-10-30).
  const hasLetter = generating || (isTriage ? letterKinds.length > 0 : !!ticket.suggestion && !noLetter);
  const shownSlot: LetterSlot = isTriage ? letterSlotOfKind(shownKind, ticket.suggestion) : 'primary';
  const letterWord = isTriage && shownKind ? draftKindWord(shownKind, cm.face) : 'Entwurf';

  // ─── The footer letter check (2026-10-30) ───
  // Open fields of the letter shown: the VNB letter's live fill state; a WIM letter's served one.
  const countSlots = (segs: ScaffoldSegment[] | undefined) => (segs ?? []).filter(seg => seg.kind === 'slot').length;
  const shownSlotModel = isTriage && shownKind ? draftModel.slots.find(slot => slot.kind === shownKind) ?? null : null;
  const fields: FieldsState | null = ticket.status !== 'suggestion_ready' ? null
    : isTriage
      ? (shownSlotModel?.scaffold ? { total: countSlots(shownSlotModel.scaffold.segments), open: openSlotCount(shownSlotModel) } : null)
      : (scaffold ? { total: countSlots(scaffold.segments), open: draft.openCount } : null);
  const checkPills: CheckPill[] | null = hasLetter && !generating && ticket.suggestion
    ? composeCheckPills(ticket.suggestion.letterCheck, shownSlot, fields) : null;
  // The verdict after the press takes the pills' slot, on every row — a letter or none.
  const approvedClaims = Object.fromEntries(Object.entries(ticket.suggestion?.claims ?? {})
    .filter(([, c]) => c?.via !== 'copy')) as Partial<Record<DraftKind, DraftClaim>>;
  const decidedRow = settled || (archived && isSettledStatus(ticket.previousStatus ?? ''));
  const hasApproval = resolveDecisionKinds(approvedClaims).length > 0;
  const undoHere = !!afterPress && afterPress.uuid === ticket.uuid && afterPress.undoLive && afterPress.mode === 'line';
  const verdict = decidedRow && (declined || hasApproval) ? (
    <>
      {declined ? (
        <DecisionBadge act="instead" hue={declineInsteadHue(cm, ticket.decline)} word={declineWord} label={DECLINE_LABEL} variant="detail" testId="decline-instead-pill" />
      ) : (
        <span data-testid="detail-line-entscheidung" style={{ display: 'inline-flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <DecisionBadges claims={claimedKinds(approvedClaims)} decidedAt={decisionClaimAt(approvedClaims)} variant="detail" primaryAxis={ticket.primaryAxis} />
        </span>
      )}
      {undoHere && (
        <button type="button" data-testid="footer-undo" onClick={afterPress!.onUndo} style={miniButtonStyle}>Rückgängig</button>
      )}
    </>
  ) : null;
  // The collapsed undo line is the footer's own verdict row when the decided mail is on screen.
  const barCard = cardView && undoHere && verdict ? null : cardView;
  // „Felder" takes the clerk to the first open field where it is filled today (the docked list).
  const goToFirstField = () => {
    const root = document.querySelector<HTMLElement>(`[data-letter-area="${ticket.uuid}"]`);
    const input = Array.from(root?.querySelectorAll<HTMLInputElement>('input[id^="klaerfall-slot-"]:not([readonly])') ?? [])
      .find(el => !el.closest('[hidden]'));
    if (!input) return;
    input.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    input.focus({ preventScroll: true });
  };
  const pickCardRow = (row: CheckCardRow, kind: CheckPill['kind']) => {
    setEditMode(false);
    setPinnedPill(kind);
    openCompare({ mail: row.mailQuote, letter: row.letterQuote });
  };
  const footerCheck = (
    <LetterCheckFooter
      pills={checkPills}
      verdict={verdict}
      letterWord={letterWord}
      resetKey={ticket.uuid}
      letterKey={shownSlot}
      onPickRow={pickCardRow}
      onFields={goToFirstField}
      fieldsHighlight={approveHover && !!fields && fields.open > 0}
      pinnedKind={compareOpen ? pinnedPill : null}
      onPillPress={() => setPinnedPill(null)}
    />
  );

  const bottomBar = (
    <div data-testid="detail-footer" style={{ marginTop: theme.spacing.sm, borderTop: `1px solid ${theme.colors.borderLight}` }}>
      {footerCheck}
      <div style={{
        paddingTop: theme.spacing.md, minHeight: 48,
        borderTop: checkPills || verdict ? `1px solid ${theme.colors.borderLight}` : undefined,
      }}>
      {pressSlots ? (
        <PressBar
          slots={pressSlots}
          onAct={onAct}
          decline={{
            status: ticket.status, previousStatus: ticket.previousStatus, primaryAxis: ticket.primaryAxis,
            writesBlocked: !onDecline || writesBlocked || busyDraftKind !== null, maintenance,
            onDecline: onDecline ?? (async () => {}), openRequest: declineRequest,
          }}
          card={barCard}
          message={pressMessage}
          onDismissMessage={onDismissPressMessage}
          slotOne={isTriage ? triageSlotOneFaces(draftModel, cm.face) : letterSlotOneFaces()}
          onSlotOneHover={setApproveHover}
        />
      ) : triageLoading ? (
        <div data-testid="press-bar-loading" style={{ display: 'grid', gridTemplateColumns: SLOT_COLUMNS, gap: theme.spacing.sm }}>
          {[0, 1, 2].map(n => <SkeletonBlock key={n} width="100%" height={40} borderRadius={theme.borderRadius.md} />)}
        </div>
      ) : (
      <div style={{
        // Wraps as WHOLE slots on a narrow pane: a slot never shrinks or wraps its label.
        display: 'flex', flexWrap: 'wrap', gap: theme.spacing.sm,
        opacity: btnFade === 'in' ? 1 : 0, transition: 'opacity 0.15s ease',
      }}>
        {archived ? (
          // The Archiv: the letter stays reachable (an Erledigt row approved with a letter), then
          // Wiederherstellen — which returns the row to where it was filed from, verdict standing.
          <>
            {ticket.hasDraft && !noDraft && !letterBlank && !isTriage && (ticket.previousStatus === 'approved' || ticket.previousStatus === 'sent') && (
              <ActionButton label="Entwurf herunterladen" onClick={onDownload} bg={theme.colors.navy} />
            )}
            {canRestore && (
              <ActionButton label={actionLoading ? '...' : 'Wiederherstellen'} onClick={onRestore!} disabled={actionLoading || maintenance} bg={theme.colors.navy} />
            )}
          </>
        ) : (
          <>
            {canReprocess && (
              <ActionButton label={actionLoading ? '...' : 'Erneut verarbeiten'} onClick={onReprocess!} disabled={actionLoading || maintenance} bg={theme.colors.error} />
            )}
            {onGoToUploads && !canReprocess && !ticket.hasDraft && (
              <ActionButton label="Zur Verarbeitung" onClick={onGoToUploads} bg={theme.colors.navy} />
            )}
            {/* A failed row's housekeeping: Archivieren beside Erneut verarbeiten — two slots. */}
            {canReprocess && canArchive && (
              <ActionButton label={actionLoading ? '...' : 'Archivieren'} onClick={onArchive!} disabled={actionLoading || maintenance} />
            )}
          </>
        )}
      </div>
      )}
      </div>
    </div>
  );

  // The action-error banner, built ONCE and rendered in exactly one of its two homes: the pane
  // while compare mode is closed, the stage's pinned top slot while it is open.
  const errorBanner = error ? (
    <div
      data-testid="detail-action-error"
      style={{
        padding: theme.spacing.md, backgroundColor: '#fef2f2', color: theme.colors.error,
        borderRadius: theme.borderRadius.md, marginBottom: theme.spacing.md,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
      }}
    >
      {error}
      <button onClick={onClearError} aria-label="Meldung schließen" style={{
        background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.error, display: 'inline-flex', padding: 2,
      }}>
        <CrossIcon size={12} color={theme.colors.error} />
      </button>
    </div>
  ) : null;

  // ─── The header (registry `ticketDetail_face`, rule 8) ───
  // Born from the row click: reassure (the subject and what the mail is), arm the adversarial read
  // (the warnings, the confidence) and make the fork's next click obvious (the paperclip). Order on
  // the meta line: badge · Erzwungen · Stufe · attributes · flags · partner · values · paperclip.
  // The clerk traces are not here (the letter's heading line carries them); the verdict stands in
  // the footer's pill slot after the press. The compare stage and the pop-out reuse these lines.
  const renderHeader = (inStage: boolean) => {
    const tid = (id: string) => (inStage ? undefined : id);
    const enforced = ticket.suggestion?.flags?.filter(f => f.enforced) ?? [];
    const shownFlags = orderedFlags(visibleFlags(cm, enforced.map(f => f.id), ticket.primaryAxis));
    const stufe = (ticket.eskalationsstufe ?? 0) >= 1 ? Math.min(Math.max(ticket.eskalationsstufe!, 1), 3) as 1 | 2 | 3 : null;
    const fadeKey = [stufe ?? '', ...shownFlags].join(',');
    const arrivalText = arrivalHoverText(ticket.receivedAt, ticket.createdAt);
    const valueFaces = ticket.primaryAxis && ticket.secondaryAxis
      ? ticket.secondaryAxis.map(code => pillFace('axis2', cm.secondaryAxisText(code)))
      : [];
    return (
      <div data-testid={tid('detail-header')} style={{ marginBottom: inStage ? theme.spacing.sm : theme.spacing.md, borderBottom: `1px solid ${theme.colors.borderLight}`, paddingBottom: theme.spacing.sm, flexShrink: 0 }}>
        {/* The stage has the width: it shows the subject whole there. */}
        <SubjectLine subject={ticket.subject || '(Kein Betreff)'} hover={[ticket.customerEmail, arrivalText].filter(Boolean).join(' · ')} copy={inStage} fixedHeight={!inStage} />
        <div data-testid={tid('detail-line-meta')} style={headerLineStyle}>
          <span style={headerMetaGroupStyle}>
            {ticket.primaryAxis && <CategoryBadge category={ticket.primaryAxis} style={{ fontSize: 11, padding: '2px 8px', flexShrink: 0 }} />}
            {ticket.primaryAxis && ticket.isForceOverride && (
              <span data-testid={tid('detail-erzwungen')} style={pillStyle('info', 'row')}>{pillFace('info', 'erzwungen')}</span>
            )}
            <span key={fadeKey} style={{ display: 'contents' }}>
              {stufe && <StufeChip n={stufe} variant="row" style={{ animation: 'stx-fadeIn 0.3s ease' }} />}
            </span>
            {/* The classified-alongside attributes (Gas, …) — facts about the MAIL, outside any axis gate. */}
            <TicketAttributePills ticket={ticket} variant="row" />
            {shownFlags.map(id => (
              <span key={id} style={{ display: 'inline-flex', flexShrink: 0, animation: 'stx-fadeIn 0.3s ease' }}>
                <FlagWord id={id} hover={flagQuoteHover(enforced.find(f => f.id === id))} />
              </span>
            ))}
            {/* Shown when the sender register knows the sender's company — no role switch. */}
            {senderRegistryServed && ticket.senderOrganisation && (
              <SenderPill organisation={ticket.senderOrganisation} senderKind={ticket.senderKind ?? null} email={null}
                style={{ animation: 'stx-fadeIn 0.3s ease', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', flexShrink: 1, minWidth: 0 }} />
            )}
            {valueFaces.length > 0 && <HeaderValues faces={valueFaces} />}
          </span>
          {paperclip(inStage)}
        </div>
        <div data-testid={tid('detail-line-reason')} style={{ ...headerLineStyle, justifyContent: 'space-between' }}>
          {/* The reason line's slot (end shape `header_reason_line`): reserved and blank in this
              build, so nothing moves when it lands. It gives way; the right group never truncates. */}
          <span data-testid={tid('detail-reason-slot')} style={{ flex: '1 1 0', minWidth: 0 }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: theme.spacing.sm, flexShrink: 0 }}>
            {ticket.primaryAxis && ticket.classifierConfidence != null && (
              <SplitPill label={AI_DESCRIPTOR.classification} variant="detail" testId={inStage ? undefined : 'klassifikationskonfidenz-pill'}>
                {formatPercentFace(ticket.classifierConfidence)}
              </SplitPill>
            )}
            {/* The retry count qualifies the confidence: its one home on the pane. */}
            {ticket.reanalysisCount != null && ticket.reanalysisCount > 0 && (
              <ClerkTrace kind="reanalysis" count={ticket.reanalysisCount} variant="detail" testId={inStage ? 'reanalysis-count-stage' : undefined} />
            )}
          </span>
        </div>
      </div>
    );
  };

  // ─── The safety notice (2026-10-30) ───
  // The house lines the Begründung carried — the fixed danger procedure and the attack warning —
  // survive the box: ONE compact notice under the header, above the letter, on every role and only
  // on a row that carries one. Served text unchanged; nothing reserved where there is none (it lands
  // in the same paint as the letter).
  const safetyNotice = (inStage: boolean) => {
    const procedure = ticket.suggestion?.reasoning?.procedure ?? null;
    const warning = ticket.suggestion?.reasoning?.warning ?? null;
    if (!procedure && !warning) return null;
    return (
      <div
        role="note"
        data-testid={inStage ? undefined : 'safety-notice'}
        style={{
          marginBottom: theme.spacing.md, padding: '6px 10px', fontSize: 12, lineHeight: 1.5, flexShrink: 0,
          borderLeft: `3px solid ${procedure ? theme.colors.warning : theme.colors.navy}`,
          backgroundColor: procedure ? theme.colors.warningTint : theme.colors.navyTint,
          borderRadius: theme.borderRadius.sm, color: theme.colors.navyMid,
          display: 'flex', flexDirection: 'column', gap: 2,
        }}
      >
        {procedure && (
          <span data-testid={inStage ? undefined : 'safety-procedure'}>
            <strong style={{ color: theme.colors.warning }}>Vorgehen (fest hinterlegt):</strong> {procedure}
          </span>
        )}
        {warning && (
          <span data-testid={inStage ? undefined : 'safety-warning'} style={{ color: theme.colors.navy }}>{warning}</span>
        )}
      </div>
    );
  };

  // ─── The mail (the body of a row without a letter; compare mode's left column) ───
  const mailBody = ticket.emailBodyPreview ? (
    isHtml(ticket.emailBodyPreview) ? (
      <div
        data-testid="mail-body"
        style={mailBoxStyle}
        dangerouslySetInnerHTML={{
          __html: styleRedactionTokens(DOMPurify.sanitize(ticket.emailBodyPreview, {
            FORBID_TAGS: ['style', 'link', 'meta', 'head', 'title', 'script', 'img'],
            FORBID_ATTR: ['style', 'class'],
          })),
        }}
      />
    ) : (
      <div
        data-testid="mail-body"
        style={{ ...mailBoxStyle, whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: styleRedactionTokens(escapeHtmlText(ticket.emailBodyPreview)) }}
      />
    )
  ) : (
    <p style={{ margin: 0, fontSize: 13, color: theme.colors.textSecondary }}>Kein Mailtext vorhanden</p>
  );
  const summaryBoxId = `anhang-summary-${ticket.uuid}`;
  const mailColumn = (
    <>
      <AttachmentChips
        documents={cm.configLoading ? [] : anhaengeDocuments}
        inline={cm.configLoading ? [] : anhaengeInline}
        countInline={attachmentsCountInline}
        focusSeq={chipFocusSeq}
        onSummary={extractionSummary != null ? () => {
          document.getElementById(summaryBoxId)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
        } : undefined}
      />
      {mailBody}
      {extractionSummary != null && (
        <div id={summaryBoxId} style={{ marginTop: theme.spacing.md }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 3 }}>Zusammenfassung der Anhänge</div>
          <div
            data-testid="anhang-summary"
            style={{ ...mailBoxStyle, backgroundColor: theme.colors.bgPage, whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: styleRedactionTokens(escapeHtml(extractionSummary)) }}
          />
        </div>
      )}
    </>
  );

  // ─── The paperclip (header line 3, right; registry `ticketDetail_face`) ───
  // A reserved 34 px slot: the glyph and the count when the mail has attachments, blank otherwise.
  // Hover/focus lists the files (name, size, the OCR state as the chips say it); a click opens
  // compare mode with the chips focused — the path the door's count segment took before.
  const countKnown = !cm.configLoading;
  const countValue = countKnown ? anhaengeCount : anhaengeRows.length;
  const showCount = countKnown && anhaengeCount > 0;
  const listedRows = attachmentsCountInline ? anhaengeRows : anhaengeDocuments;
  const paperclip = (inStage: boolean) => {
    const title = attachmentCountTitle(countValue, attachmentsCountInline ? 0 : anhaengeInline.length);
    const button = (
      <button
        type="button"
        onClick={() => { openCompare(); setChipFocusSeq(n => n + 1); }}
        aria-label={`${title}, Mail und Brief nebeneinander öffnen`}
        data-testid={inStage ? undefined : 'detail-paperclip'}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, boxSizing: 'content-box',
          width: PAPERCLIP_SLOT_WIDTH, height: HEADER_LINE_HEIGHT, padding: '4px 0', margin: '-4px 0',
          border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: theme.font.family,
          fontSize: 12, fontWeight: 600, color: theme.colors.navy, animation: 'stx-fadeIn 0.3s ease',
        }}
      ><PaperclipIcon size={14} color={theme.colors.navy} />{countValue}</button>
    );
    return (
      <span style={{ width: PAPERCLIP_SLOT_WIDTH, flexShrink: 0, display: 'inline-flex', justifyContent: 'flex-end' }}>
        {showCount && (
          <InfoTooltip
            text={title}
            content={(
              <span data-testid={inStage ? undefined : 'detail-paperclip-list'} style={{
                display: 'flex', flexDirection: 'column', gap: 3, minWidth: 220, padding: '8px 12px', fontSize: 11.5, lineHeight: 1.5,
                backgroundColor: theme.colors.white, color: theme.colors.navy, border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.md, boxShadow: theme.shadows.cardHover,
              }}>
                <strong style={{ fontWeight: 600 }}>{title}</strong>
                {listedRows.map(row => (
                  <span key={row.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: theme.colors.navyMid }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{row.name}</span>
                    <span style={{ flexShrink: 0 }}>{[row.size != null ? formatBytes(row.size) : null, OCR_STATE_LABELS[row.state]].filter(Boolean).join(' · ')}</span>
                  </span>
                ))}
                <span style={{ color: theme.colors.textSecondary }}>Klick: Mail und Brief nebeneinander, Anhänge im Fokus</span>
              </span>
            )}
          >{button}</InfoTooltip>
        )}
      </span>
    );
  };

  // ─── The door into compare mode: the word and its icon ───
  const door = (
    <button
      type="button"
      onClick={() => openCompare()}
      title="Mail und Brief nebeneinander (V)"
      data-testid="compare-button"
      style={{
        ...doorSegmentStyle, flexShrink: 0, padding: '1px 9px 1px 7px',
        border: `1.5px solid ${theme.colors.navy}`, borderRadius: 5, backgroundColor: theme.colors.white,
      }}
    ><CompareIcon size={14} color={theme.colors.navy} />Vergleichen</button>
  );

  // ─── The clerk traces as text on the letter's heading line (second face of ClerkTrace) ───
  const claimsForTraces = ticket.suggestion?.claims;
  const traceKinds: Exclude<ClerkTraceKind, 'reanalysis'>[] = [];
  if (showsKopiert({
    status: ticket.status, previousStatus: ticket.previousStatus, copyApprovedAt: ticket.copyApprovedAt,
    claimVia: Object.fromEntries(Object.entries(claimsForTraces ?? {}).map(([kind, c]) => [kind, c?.via ?? 'approve'])),
  })) traceKinds.push('kopiert');
  if (ticket.suggestion?.editedText) traceKinds.push('bearbeitet');
  if (claimedKinds(claimsForTraces).includes('ack')) traceKinds.push('eingang_bestaetigt');

  // ─── The letter head: its title (or, with two letters, the switch) and the door ───
  const letterSwitch = isTriage && letterKinds.length > 1 && shownKind ? (
    <span role="group" aria-label="Brief" data-testid="letter-switch" style={{
      display: 'inline-flex', border: `1px solid ${theme.colors.borderLight}`, borderRadius: 6, overflow: 'hidden',
    }}>
      {letterKinds.map(kind => (
        <button
          key={kind}
          type="button"
          aria-pressed={kind === shownKind}
          onClick={() => {
            setShownPick({ uuid: ticket.uuid, kind });
            // A letter resting behind its strip opens when the switch picks it.
            setDisclosed(ticket.uuid, kind, true);
            setMarks(null);
          }}
          style={{
            fontFamily: theme.font.family, fontSize: 11.5, border: 'none', padding: '2px 10px', cursor: 'pointer',
            backgroundColor: kind === shownKind ? theme.colors.navy : theme.colors.white,
            color: kind === shownKind ? theme.colors.white : theme.colors.navyMid,
            fontWeight: kind === shownKind ? 600 : 500,
          }}
        >{draftKindWord(kind, cm.face)}</button>
      ))}
    </span>
  ) : null;
  const letterTitle = (
    <>
      <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>KI-Vorschlag</span>
      {letterSwitch}
    </>
  );
  const letterHead = hasLetter ? (
    <div data-testid={compareOpen ? undefined : 'letter-head'} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 26, marginBottom: theme.spacing.sm, flexShrink: 0 }}>
      {letterTitle}
      <ClerkTraceText kinds={traceKinds} receiptTitle={copyToastMessage('ack', cm.face)} />
      {compareOpen ? null : door}
    </div>
  ) : null;

  const sugg = ticket.suggestion ?? null;
  const isVnbScaffold = !!sugg?.scaffold;
  const suggDisplayedText = sugg ? (sugg.editedText ?? sugg.suggestionText) : '';
  const suggIsHtml = /<[a-z][\s\S]*>/i.test(suggDisplayedText);
  const suggSplit = suggIsHtml ? splitEditableRegion(suggDisplayedText) : null;
  const canEditSugg = !!suggSplit && ticket.status === 'suggestion_ready';
  const wasEditedSugg = !!sugg?.editedText;
  // ONE box for both letter buttons (Bearbeiten / the X): the flip between them changes the width
  // only — never the height, never the neighbours.
  const kiHeaderBtnBox: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4, boxSizing: 'border-box',
    height: 22, lineHeight: '16px',
    padding: '2px 10px', fontSize: 11, fontWeight: 500, fontFamily: theme.font.family,
    backgroundColor: 'transparent', borderRadius: theme.borderRadius.sm,
  };
  // Bearbeiten sits after the letter, right-aligned beside Kopieren — in the pane and in compare
  // mode's letter column, where it opens the editor with the marked sentence selected. While the
  // editor is the body the verbs stand hidden in place (the editor's Abbrechen is the one cancel). No Bearbeiten on a triage
  // row (its cards carry their own), a fill-in letter (its checklist is its edit) or a filing.
  const startEdit = () => {
    if (!canEditSugg) return;
    if (compareOpen && marks?.letter) {
      pendingSelect.current = marks.letter;
      setMarks(m => (m ? { ...m, letter: null } : m));
    }
    editFocusArmed.current = true;
    setEditMode(true);
  };
  const bearbeitenAction = (sugg && !isTriage && !isVnbScaffold && !generating && !fileProposal) ? (
    <button
      ref={bearbeitenRef}
      onClick={startEdit}
      disabled={!canEditSugg}
      title={canEditSugg
        ? (wasEditedSugg ? 'Bearbeitung anpassen' : 'KI-Vorschlag bearbeiten')
        : (ticket.status !== 'suggestion_ready'
            ? 'Bearbeitung nur vor Genehmigen möglich'
            : 'Bearbeitung erfordert Neuverarbeitung mit neuem Prompt')}
      style={{
        ...kiHeaderBtnBox,
        color: canEditSugg ? theme.colors.navyMid : theme.colors.textSecondary,
        border: `1px solid ${canEditSugg ? theme.colors.borderLight : theme.colors.bgLight}`,
        cursor: canEditSugg ? 'pointer' : 'not-allowed',
        opacity: canEditSugg ? 1 : 0.5,
      }}
    ><PencilIcon size={11} color={canEditSugg ? theme.colors.navyMid : theme.colors.textSecondary} />Bearbeiten</button>
  ) : null;
  // Kopieren, beside Bearbeiten at the end of the letter. Copy-approval (#4): copying the
  // suggestion in Offen records an indirect approval. In suggestion_ready it is the primary "grab"
  // affordance (filled navy); post-approval it stays a quiet bordered re-copy that does NOT
  // re-credit. Gate: the same slots-filled predicate as canApprove. Not mounted on a triage row
  // (each card owns its own Kopieren), a no-letter row, or while the editor is open.
  const kopierenAction = (sugg && !isTriage && !generating && !noLetter) ? (() => {
    const isOffen = ticket.status === 'suggestion_ready';
    const prominent = isOffen && !copyFeedback;
    const slotsAllFilled = !scaffold || draft.allConfirmed;
    const kopiereBlocked = isOffen && !slotsAllFilled;
    return (
      <button
        disabled={kopiereBlocked}
        title={kopiereBlocked ? `${openSlotsNote(draft.openCount)} – bitte zuerst alle Felder ausfüllen` : undefined}
        onClick={() => {
          if (kopiereBlocked) return;
          // editedText wins over suggestionText — the clerk's own letter, never the model's
          // original wording. Strip HTML tags for clean clipboard text.
          const plain = (sugg.editedText ?? sugg.suggestionText).replace(/<[^>]*>/g, '');
          navigator.clipboard.writeText(plain).then(() => {
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 1500);
            // Indirect approval, Offen only; skipped during maintenance (the server 423s writes).
            if (ticket.status === 'suggestion_ready' && !maintenance) onCopyApprove?.();
          });
        }}
        style={{
          ...kiHeaderBtnBox,
          fontWeight: prominent ? 600 : 500,
          color: copyFeedback ? theme.colors.success : prominent ? theme.colors.white : theme.colors.navyMid,
          backgroundColor: prominent ? theme.colors.navy : 'transparent',
          border: `1px solid ${copyFeedback ? theme.colors.success : prominent ? theme.colors.navy : theme.colors.borderLight}`,
          cursor: kopiereBlocked ? 'not-allowed' : 'pointer',
          opacity: kopiereBlocked ? 0.6 : 1,
          transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
        }}
      >
        {copyFeedback ? 'Kopiert!' : 'Kopieren'}
      </button>
    );
  })() : null;
  const letterVerbs = (bearbeitenAction || kopierenAction) ? (
    // While the editor is open its own Abbrechen / Speichern pair is the way out; the verbs keep
    // their place, invisible, so nothing below the letter moves as the editor opens or closes.
    <span data-testid="letter-verbs" aria-hidden={editMode || undefined} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: theme.spacing.xs, flexShrink: 0, visibility: editMode ? 'hidden' : undefined }}>
      {bearbeitenAction}{kopierenAction}
    </span>
  ) : null;

  // ─── The letter itself (read view; Bearbeiten swaps the editor in, in place) ───
  const letterContent = (() => {
    if (!sugg) return null;
    if (sugg.triage) {
      return (
        <TriageDraftArea
          ticket={ticket}
          model={draftModel}
          onRender={handleRenderDraft}
          // The WRAPPED copy (see handleCopyDraft) — never the host's raw handler.
          onCopyDraft={onCopyDraft ? handleCopyDraft : undefined}
          shownKind={shownKind}
          onShowKind={kind => { if (isDraftDecisionKind(kind)) setShownPick({ uuid: ticket.uuid, kind }); }}
          onEditDraft={handleEditDraft}
          busyKind={busyDraftKind}
          holdVisible={holdVisible}
        />
      );
    }
    // No letter on this row: the mail is the body (above); nothing more is drawn here.
    if (noLetter) return null;
    const displayedText = sugg.editedText ?? sugg.suggestionText;
    const isHtmlSuggestion = /<[a-z][\s\S]*>/i.test(displayedText);
    const split = isHtmlSuggestion ? splitEditableRegion(displayedText) : null;
    const baseStyle: React.CSSProperties = {
      padding: theme.spacing.md, backgroundColor: theme.colors.bgPage,
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.colors.borderLight}`,
      fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
    };
    // VNB scaffold → the fill-in Klärfall editor. Historical engine override tickets carry the
    // letter ONLY in suggestionText with empty segments — synthesize the single text segment.
    if (sugg.scaffold) {
      const scaffoldSegments = sugg.scaffold.segments.length > 0
        ? sugg.scaffold.segments
        : [{ kind: 'text' as const, value: displayedText }];
      return (
        <>
          <KlaerfallEditor
            segments={scaffoldSegments}
            marktprozess={ticket.primaryAxis ?? ''}
            zone={sugg.replyEngine?.zone}
            draft={draft}
            simulateEnabled={canSimulate}
            onSimulate={handleSimulate}
          />
          {editError && (
            <div role="alert" style={{ marginTop: theme.spacing.xs, fontSize: 12, color: theme.colors.error }}>
              {editError}
            </div>
          )}
        </>
      );
    }
    if (editMode && split) {
      return <SuggestionEditor
        split={split}
        saving={editSaving}
        error={editError}
        editableRef={editableRef}
        baseStyle={baseStyle}
        onCancel={cancelEdit}
        onSave={async () => {
          if (!editableRef.current) return;
          setEditSaving(true); setEditError(null);
          const newInner = editableRef.current.innerHTML;
          const fullHtml = split.prefix + newInner + split.suffix;
          const res = await tm.editSuggestion(ticket.uuid, fullHtml);
          setEditSaving(false);
          if (res.success) {
            editFocusArmed.current = true;
            setEditMode(false);
            onSuggestionEdited?.();
          } else {
            setEditError(res.error || 'Fehler beim Speichern');
          }
        }}
      />;
    }
    return isHtmlSuggestion ? (
      <div
        data-testid="suggestion-letter"
        style={baseStyle}
        dangerouslySetInnerHTML={{ __html: styleRedactionTokens(DOMPurify.sanitize(displayedText)) }}
      />
    ) : (
      <div
        data-testid="suggestion-letter"
        style={{ ...baseStyle, whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: styleRedactionTokens(displayedText) }}
      />
    );
  })();

  // The AI descriptors under the letter (registry `ai_descriptors`): „Vorschlagskonfidenz | 86 %",
  // „KI-Antwortzeit | 10,7 s"; a row no composer wrote a letter for says „Kein Entwurf | Von Hand".
  // Then the Excel-Zeile copy where a form was read, then the letter verbs, right-aligned.
  const descriptors = ticket.suggestion && !generating ? (
    <div data-testid="ai-descriptors" style={{ marginTop: theme.spacing.sm, minHeight: Math.max(SPLIT_PILL_HEIGHT.detail, 22), display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing.sm }}>
      {!isTriage && noLetter && (
        <SplitPill label={AI_DESCRIPTOR.noDraft} variant="detail" testId="kein-entwurf-pill">{NO_DRAFT_FACE}</SplitPill>
      )}
      {!noLetter && ticket.suggestion.suggestionConfidence != null && (
        <SplitPill label={AI_DESCRIPTOR.suggestion} variant="detail" testId="vorschlagskonfidenz-pill">
          {formatPercentFace(ticket.suggestion.suggestionConfidence)}
        </SplitPill>
      )}
      {ticket.suggestion.processingTimeMs != null && (
        <SplitPill label={AI_DESCRIPTOR.responseTime} variant="detail" testId="ki-antwortzeit-pill">
          {formatSecondsFace(ticket.suggestion.processingTimeMs)}
        </SplitPill>
      )}
      {/* Form-Excel row copy: mounted only where OCR recognized a form (fetched on click). */}
      {ticket.hasFormExtraction && (
        <button
          title={ticket.formSourceFileName
            ? `Excel-Zeile kopieren — Formular aus: ${ticket.formSourceFileName}`
            : 'Excel-Zeile kopieren'}
          onClick={() => {
            // A refusal is not an error: the server declines to put a row with something to check
            // on the clipboard, and says why.
            smarttix.copyFormRow(ticket.uuid)
              .then((res) => {
                if (res.blocked || !res.row) {
                  const why = res.reason
                    ?? 'Diese Zeile wird nicht kopiert. Bitte den Formular-Excel-Download verwenden.';
                  if (onInfo) onInfo(why); else { setRowError(true); setTimeout(() => setRowError(false), 2500); }
                  return;
                }
                return navigator.clipboard.writeText(res.row).then(() => {
                  setRowFeedback(true);
                  setTimeout(() => setRowFeedback(false), 1500);
                });
              })
              .catch(() => {
                setRowError(true);
                setTimeout(() => setRowError(false), 1500);
              });
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 10px', fontSize: 11, fontWeight: 500, fontFamily: theme.font.family,
            color: rowError ? theme.colors.deltaNegative : rowFeedback ? theme.colors.success : theme.colors.navyMid,
            backgroundColor: 'transparent',
            border: `1px solid ${rowError ? theme.colors.deltaNegative : rowFeedback ? theme.colors.success : theme.colors.borderLight}`,
            borderRadius: theme.borderRadius.sm, cursor: 'pointer',
          }}
        >
          <SpreadsheetRowIcon size={14} color={rowError ? theme.colors.deltaNegative : rowFeedback ? theme.colors.success : theme.colors.navyMid} />
          {rowError ? 'Fehler' : rowFeedback ? 'In Zwischenablage!' : 'Excel-Zeile'}
        </button>
      )}
      {letterVerbs}
    </div>
  ) : null;

  return (
    <div style={{ fontFamily: theme.font.family, display: 'flex', flexDirection: 'column', height: '100%', animation: 'stx-fadeIn 0.3s ease' }}>
      {/* The banner's in-flow HOME — empty while the stage holds it, holding the height it had. */}
      <div
        ref={errorSlotRef}
        style={compareOpen ? { height: errorHeight.current, flexShrink: 0 } : undefined}
      >
        {compareOpen ? null : errorBanner}
      </div>

      {/* Pipeline error banner */}
      {isPipelineError && (
        <div style={{
          padding: theme.spacing.md, backgroundColor: '#fef2f2', color: theme.colors.error,
          borderRadius: theme.borderRadius.md, marginBottom: theme.spacing.md, fontSize: 13, lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Pipeline-Fehler{stageLabel ? ` bei ${stageLabel}` : ''}
            {ticket.retryCount != null && ticket.retryCount > 1 && (
              <span style={{ fontWeight: 400, marginLeft: 8 }}>({ticket.retryCount} Versuche)</span>
            )}
          </div>
          {ticket.errorMessage && (
            <div style={{ fontSize: 12, color: '#991b1b', fontFamily: 'monospace', wordBreak: 'break-word' }}>
              {ticket.errorMessage}
            </div>
          )}
        </div>
      )}

      {renderHeader(false)}
      {safetyNotice(false)}

      {/* The reading area. ONE scroller at a time: while compare mode is open its columns scroll, and
          this panel keeping an `auto` of its own would put a second scrollbar behind them. */}
      <div
        data-testid="detail-sections-scroll"
        // No scroll anchoring: the letter swapping for the taller editor must not drag the view.
        style={{ flex: 1, overflowY: compareOpen ? 'visible' : 'auto', overflowAnchor: 'none' }}
      >
        {/* A row without a letter reads its mail as the body, the Begründung's summary as one line
            under it (2026-10-30). The door sits on the mail's own head: attachments live in compare mode. */}
        {!hasLetter && (
          <div data-testid="mail-section" style={{ marginBottom: theme.spacing.md }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 26, marginBottom: theme.spacing.sm }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>E-Mail</span>
              <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>{compareOpen ? null : door}</span>
            </div>
            {mailBody}
            {ticket.suggestion?.reasoning?.summary && (
              <p data-testid="mail-summary-line" style={{ margin: `${theme.spacing.sm}px 0 0`, fontSize: 12, lineHeight: 1.5, color: theme.colors.textSecondary }}>
                {ticket.suggestion.reasoning.summary}
              </p>
            )}
          </div>
        )}

        <div data-letter-area={ticket.uuid} style={{ marginBottom: hasLetter ? theme.spacing.lg : 0 }}>
          <CompareStage
            open={compareOpen}
            onClose={closeCompare}
            title="Mail und Brief nebeneinander"
            header={<>{renderHeader(true)}{safetyNotice(true)}</>}
            mail={mailColumn}
            letterHead={letterHead}
            letterless={!hasLetter}
            footer={bottomBar}
            banner={errorBanner}
            topInset={stageTopInset}
            marks={marks}
          >
            <div
              style={{
                position: 'relative',
                // While no suggestion exists yet (fresh Verarbeiten) the overlay needs a canvas to
                // paint on — ~140px ≈ one suggestion height.
                minHeight: !ticket.suggestion && generating ? 140 : undefined,
              }}
              onClickCapture={e => {
                const btn = (e.target as HTMLElement).closest?.('button[aria-label^="Bearbeiten:"]');
                if (!btn || !compareOpen || !marks?.letter) return;
                const quote = marks.letter;
                setMarks(m => (m ? { ...m, letter: null } : m));
                setCardSelect(c => ({ quote, seq: (c?.seq ?? 0) + 1 }));
              }}
            >
              {letterContent}
              {/* A stale suggestion stays visible but overlaid with animated scanlines while the new one generates. */}
              {generating && (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: theme.borderRadius.md,
                  overflow: 'hidden',
                  animation: 'stx-fadeIn 0.3s ease',
                }}>
                  <div data-stx-scanlines style={{
                    position: 'absolute', inset: -50,
                    backgroundImage: `repeating-linear-gradient(
                      -45deg,
                      transparent, transparent 8px,
                      rgba(0, 13, 102, 0.06) 8px, rgba(0, 13, 102, 0.06) 16px
                    )`,
                    backgroundSize: '22.6px 22.6px',
                    animation: 'stx-scanlines 1s linear infinite',
                  }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    backgroundColor: 'rgba(245, 246, 250, 0.75)',
                  }} />
                  <div style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                    fontSize: 13, fontWeight: 500, color: theme.colors.navyMid,
                  }}>
                    <Spinner size={18} />
                    {ticket.status === 'categorizing' && 'Wird kategorisiert...'}
                    {ticket.status === 'categorized' && 'Kategorisiert — Vorschlag wird vorbereitet...'}
                    {ticket.status === 'generating_suggestion' && 'Vorschlag wird generiert...'}
                    {ticket.status === 'reanalyzing' && 'Neuer Vorschlag wird generiert...'}
                  </div>
                </div>
              )}
            </div>
            {descriptors}
          </CompareStage>
        </div>

        {/* Personal Data — per-entry pills with dismiss + optional "always ignore" */}
        {ticket.personalData && Object.keys(ticket.personalData).length > 0 && (() => {
          // Why: dates aren't PII (GDPR) — letter/appointment dates don't identify a person
          const PII_SKIP_FIELDS = new Set(['dates']);
          const groups = Object.entries(ticket.personalData)
            .filter(([key]) => !PII_SKIP_FIELDS.has(normalizeKey(key)))
            .map(([key, value]) => {
              const nk = normalizeKey(key);
              const label = PERSONAL_DATA_LABELS[nk] || key;
              const entries = (Array.isArray(value) ? value as string[] : [String(value)])
                .filter(v => !dismissedPii.has(`${key}:${v}`))
                .filter(v => !piiIgnoreList?.some(e => e.fieldType === nk && e.value === v));
              return { key, nk, label, entries };
            })
            .filter(g => g.entries.length > 0);

          return groups.length > 0 ? (
            <PanelSection title="Erkannte Personendaten">
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, minHeight: 28 }}>
                {groups.map(({ key, nk, label, entries }) => (
                  <div key={key}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 3 }}>{label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {entries.map(v => (
                        <PiiPill
                          key={`${key}:${v}`}
                          value={v}
                          onDismiss={() => setDismissedPii(prev => new Set(prev).add(`${key}:${v}`))}
                          onAlwaysIgnore={onPiiIgnore ? () => onPiiIgnore(nk, v) : undefined}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </PanelSection>
          ) : null;
        })()}

        {/* Metadata */}
        <PanelSection title="Details">
          {/* Addressable because its GEOMETRY is a guarantee, not an implementation detail: the row
              order and the cell count are what item 5's toggle must not disturb, and a test can only
              assert that by looking at this element's children. */}
          <div data-testid="ticket-detail-meta-grid"
               style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, fontSize: 13 }}>
            <MetaItem label="UUID" value={ticket.uuid} mono copyable />
            {/* mig 072 — the MAIL FILE's own name, beside the UUID because that is where the operator
                asked for it. ⚠ NOT the same fact as the Formular-Herkunft tooltip further up: that one
                names an ATTACHMENT INSIDE the mail (mig 069), this one names the file that was dropped.
                ⚠ RENDERED ALWAYS, em-dash when absent — never conditionally mounted. Every pre-072
                ticket is permanently NULL here (unbackfillable: ingestion discarded the value), so a
                conditional row would appear and disappear between tickets and shift this grid on every
                selection. The two conditional siblings below (Vorheriger Status, Archiviert am) are the
                outliers, not this row — do not "harmonise" it toward them. */}
            <MetaItem label="Quelldatei" value={ticket.sourceMailFileName || '—'} truncate copyable />
            <MetaItem label="Status" value={statusLabel(ticket.status, cm.face, cm.configLoading)} />
            {ticket.previousStatus && (
              <MetaItem label="Vorheriger Status" value={statusLabel(ticket.previousStatus, cm.face, cm.configLoading)} />
            )}
            {ticket.archivedAt && (
              <MetaItem label="Archiviert am" value={formatDate(ticket.archivedAt)} />
            )}
            <MetaItem label="Erstellt" value={formatDate(ticket.createdAt)} />
            <MetaItem label="Aktualisiert" value={formatDate(ticket.updatedAt)} />
            <MetaItem label="Entwurf" value={ticket.hasDraft ? 'Ja' : 'Nein'} />
            {/* mig 072 - a SEPARATE fact from Quelldatei (the file's NAME vs the ROUTE it arrived by);
                the 069 chapter warns against collapsing two facts onto one label. Shown only when the
                tenant asks for it (UI_SHOW_SOURCE_CHANNEL) — the column exists mainly for the coming
                direct mail integration, and the row was rejected as a default at operator E2E.
                ⚠ LAST CELL, AND THAT POSITION IS THE DESIGN, not tidiness. The rule against layout
                shift guards what a user is LOOKING AT; toggling this setting happens on another page,
                so that shift is nobody's problem. But the flag also arrives ASYNCHRONOUSLY from
                /config, which CAN land while a detail is already on screen (a pop-out renders its
                ticket immediately). Mounted mid-grid, that late arrival pushes every following row
                down — measured. Mounted last, it has nothing to push. The earlier placeholder-cell
                approach solved the same problem by leaving a visible gap, which is what the operator
                rejected.
                ⚠ Do NOT move this row up beside Quelldatei for tidiness — the guard in
                TicketDetail.eingangswegToggle.test.tsx exists to catch exactly that. */}
            {showSourceChannel && <MetaItem label="Eingangsweg" value={sourceChannelLabel(ticket.sourceChannel)} />}
          </div>
        </PanelSection>
      </div>

      {/* The footer's in-flow HOME (letter check + buttons). While compare mode owns it as its pinned
          footer it renders there and nowhere else — one Genehmigen on screen, never two — and this
          slot holds the height it had, so closing lands on the pane the clerk left. */}
      <div
        ref={bottomBarSlotRef}
        style={compareOpen ? { height: bottomBarHeight.current, flexShrink: 0 } : undefined}
      >
        {compareOpen ? null : bottomBar}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════

// mig 072. The RAW vocabulary token never reaches a clerk — same model/render split as STATUS_LABELS
// above: the server SELECTS from a closed vocabulary, deterministic code RENDERS the German.
// ⚠ THE FALLBACK IS THE EM-DASH, NOT THE TOKEN. The sibling status pattern echoes its raw value when
// unmapped, which suits a vocabulary that grows server-side; this one must not. Undeclared, pre-072 and
// unrecognised-claim all mean the same thing to a clerk - nobody said - and echoing "upload" back as a
// label would present a rejected claim as a fact. Server-side normalization already makes an unknown
// token unreachable; this keeps it unreachable if that ever regresses.
const CHANNEL_LABELS: Record<string, string> = {
  upload: 'Lokal hochgeladen',
  forward: 'Aus Outlook weitergeleitet',
};
const sourceChannelLabel = (channel?: string): string =>
  (channel && CHANNEL_LABELS[channel]) || '—';

// `truncate` keeps a long value on ONE line with an ellipsis and puts the full text in the native
// title tooltip. Added for the Quelldatei row (mig 072): a mail filename can be arbitrarily long, and
// this grid is a fixed two-column layout, so an untruncated name would reflow the whole Details block
// — the layout-shift the always-rendered row exists to avoid in the first place.
// Copy-to-clipboard badge beside a MetaItem label (operator request 2026-08-31: UUID + Quelldatei
// are the two values clerks re-type elsewhere). ALWAYS mounted on a copyable row — never appears on
// hover or success, so the label row's geometry is constant (layout-stability rule). Feedback is an
// in-place icon swap to a checkmark for 1.5s, same cadence as the Kopieren button above. Disabled
// (not hidden) when the value is the absent em-dash, so pre-072 tickets keep the same geometry.
const MetaCopyBadge: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);
  const disabled = !value || value === '—';
  return (
    <button
      type="button"
      data-testid={`metacopy-${label}`}
      aria-label={`${label} in die Zwischenablage kopieren`}
      title={disabled ? undefined : 'In die Zwischenablage kopieren'}
      disabled={disabled}
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => { /* permission denial: stay silent, value remains selectable */ });
      }}
      style={{
        width: 16, height: 16, padding: 0, marginLeft: 4, border: 'none', background: 'none',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 0.65,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        verticalAlign: 'middle', color: copied ? theme.colors.success : theme.colors.textSecondary,
      }}
    >
      <CopySmallIcon size={11} done={copied} />
    </button>
  );
};

const MetaItem: React.FC<{ label: string; value: string; mono?: boolean; truncate?: boolean; copyable?: boolean }> = ({ label, value, mono, truncate, copyable }) => (
  // The CELL carries its own testid beside the value's: a layout guard needs the grid child, and
  // climbing from the value element to its parent is exactly the brittle traversal that breaks on
  // any wrapper change.
  <div data-testid={`metacell-${label}`} style={truncate ? { minWidth: 0 } : undefined}>
    <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginBottom: 2 }}>
      {label}
      {copyable && <MetaCopyBadge label={label} value={value} />}
    </div>
    <div
      // Addressable by label so a test can assert on ONE meta row's value without walking the DOM —
      // these values carry no role or accessible name of their own, and the alternative is
      // parent/sibling traversal that breaks on any wrapper change.
      data-testid={`meta-${label}`}
      // Only when truncating: the full value must stay reachable, and a title on every MetaItem would
      // put a redundant tooltip on values that are already fully visible.
      title={truncate ? value : undefined}
      style={{
        fontSize: 13, color: theme.colors.navyMid, fontWeight: 500,
        fontFamily: mono ? 'monospace' : theme.font.family,
        wordBreak: mono ? 'break-all' : undefined,
        // Fix loop pass 2, item 4 — an empty string (e.g. statusLabel/buildWorkflowToggles floating
        // to '' while config loads, fix loop pass 1 item 3) renders no inline content at all, and a
        // block with no content establishes no line box: the div collapses to 0px, not one line's
        // height, so the grid row it sits in visibly shrinks then snaps back once the real value
        // lands. minHeight reserves that one line up front so the row never moves. Measured directly
        // (headless Chromium build 1243): 13px/weight 500 Poppins renders a 20px line; 13px generic
        // monospace (the `mono` branch above) renders 15px — the two fontFamily branches don't share
        // a line height, so the floor follows the same branch the font does.
        minHeight: mono ? 15 : 20,
        ...(truncate ? { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
      }}
    >
      {value}
    </div>
  </div>
);

const PiiPill: React.FC<{
  value: string;
  onDismiss: () => void; onAlwaysIgnore?: () => Promise<void>;
}> = ({ value, onDismiss, onAlwaysIgnore }) => {
  const [confirming, setConfirming] = useState(false);

  const pillStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    backgroundColor: confirming ? theme.colors.white : theme.colors.bgLight,
    border: `1px solid ${confirming ? theme.colors.navyMuted : theme.colors.borderLight}`,
    borderRadius: theme.borderRadius.sm, fontSize: 12,
    animation: 'stx-fadeIn 0.2s ease',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  };

  if (confirming) {
    return (
      <span style={pillStyle}>
        <span style={{ color: theme.colors.navyMid, fontWeight: 500 }}>Immer ignorieren?</span>
        <button
          onClick={() => { onAlwaysIgnore?.().then(onDismiss); }}
          style={{
            padding: '1px 6px', fontSize: 11, fontWeight: 600, fontFamily: theme.font.family,
            color: theme.colors.white, backgroundColor: theme.colors.navy,
            border: 'none', borderRadius: theme.borderRadius.sm, cursor: 'pointer',
          }}
        >
          Ja
        </button>
        <button
          onClick={() => { setConfirming(false); onDismiss(); }}
          style={{
            padding: '1px 6px', fontSize: 11, fontWeight: 600, fontFamily: theme.font.family,
            color: theme.colors.navyMid, backgroundColor: 'transparent',
            border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm, cursor: 'pointer',
          }}
        >
          Nein
        </button>
      </span>
    );
  }

  return (
    <span style={pillStyle}>
      <span style={{ color: theme.colors.navyMid }}>{value}</span>
      <span
        role="button"
        tabIndex={0}
        onClick={e => {
          e.stopPropagation();
          if (onAlwaysIgnore) setConfirming(true);
          else onDismiss();
        }}
        onKeyDown={e => { if (e.key === 'Enter') { if (onAlwaysIgnore) setConfirming(true); else onDismiss(); } }}
        style={{
          cursor: 'pointer', color: theme.colors.textSecondary,
          fontSize: 14, lineHeight: 1, marginLeft: 2,
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={e => { (e.currentTarget).style.color = theme.colors.error; }}
        onMouseLeave={e => { (e.currentTarget).style.color = theme.colors.textSecondary; }}
      >
        &times;
      </span>
    </span>
  );
};

// SuggestionEditor — edit mode for KI-Vorschlag. Chrome (prefix + suffix) read-only;
// the inner Antwortvorschlag region (between data-edit-start/end markers) is contentEditable.
// Why useEffect+ref: dangerouslySetInnerHTML reflows on every render and would wipe the user's
// typing. Set innerHTML once on mount and let the browser own the edit buffer.
const SuggestionEditor: React.FC<{
  split: { prefix: string; inner: string; suffix: string };
  saving: boolean;
  error: string | null;
  editableRef: React.RefObject<HTMLDivElement | null>;
  baseStyle: React.CSSProperties;
  onSave: () => void;
  onCancel: () => void;
}> = ({ split, saving, error, editableRef, baseStyle, onSave, onCancel }) => {
  useEffect(() => {
    if (editableRef.current) {
      editableRef.current.innerHTML = styleRedactionTokens(DOMPurify.sanitize(split.inner));
    }
  }, [editableRef, split.inner]);

  const prefixHtml = styleRedactionTokens(DOMPurify.sanitize(split.prefix));
  const suffixHtml = styleRedactionTokens(DOMPurify.sanitize(split.suffix));
  // De-preambled suggestions have chrome-only prefixes (doctype/html/body) that sanitize to
  // whitespace; a padded empty box above the editor is a visible defect, so mount only real chrome.
  const hasPrefix = prefixHtml.replace(/<[^>]*>/g, '').trim().length > 0;
  const hasSuffix = suffixHtml.replace(/<[^>]*>/g, '').trim().length > 0;

  return (
    <div>
      <div style={{ ...baseStyle, border: `2px solid ${theme.colors.warning}`, padding: 0 }}>
        {hasPrefix && (
          <div
            data-testid="suggestion-chrome-prefix"
            style={{ padding: theme.spacing.md, paddingBottom: 4 }}
            dangerouslySetInnerHTML={{ __html: prefixHtml }}
          />
        )}
        <div
          ref={editableRef}
          data-testid="suggestion-editable"
          contentEditable
          suppressContentEditableWarning
          style={{
            // Explicit longhand on all four sides — a shorthand `margin` mixed with `marginTop`/
            // `marginBottom` longhand only resolves correctly by relying on style-object key order.
            marginLeft: theme.spacing.md,
            marginRight: theme.spacing.md,
            marginTop: hasPrefix ? 0 : theme.spacing.md,
            marginBottom: hasSuffix ? 0 : theme.spacing.md,
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.white,
            border: `1px dashed ${theme.colors.warning}`,
            borderRadius: theme.borderRadius.sm,
            minHeight: 80,
            outline: 'none',
          }}
        />
        {hasSuffix && (
          <div
            data-testid="suggestion-chrome-suffix"
            style={{ padding: theme.spacing.md, paddingTop: 4 }}
            dangerouslySetInnerHTML={{ __html: suffixHtml }}
          />
        )}
      </div>
      {error && (
        <div style={{
          fontSize: 12, padding: '6px 12px', marginTop: theme.spacing.sm,
          borderRadius: theme.borderRadius.sm,
          color: theme.colors.error, backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
        }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '4px 12px', fontSize: 12, fontWeight: 500, fontFamily: theme.font.family,
            color: theme.colors.navyMid, backgroundColor: 'transparent',
            border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1,
          }}
        >Abbrechen</button>
        <button
          onClick={onSave}
          disabled={saving}
          style={{
            padding: '4px 12px', fontSize: 12, fontWeight: 600, fontFamily: theme.font.family,
            color: theme.colors.white, backgroundColor: theme.colors.navy,
            border: 'none', borderRadius: theme.borderRadius.sm,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >{saving ? 'Speichern...' : 'Bearbeitung speichern'}</button>
      </div>
    </div>
  );
};

export default TicketDetailComponent;
